import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Stack, Typography, Button, Chip, Checkbox, FormControlLabel, CircularProgress,
  Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails, Table, TableHead,
  TableRow, TableCell, TableBody, DialogTitle, DialogContent, DialogActions, TextField,
  Tabs, Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';

interface Line {
  lineid: number; itemcode: string; itemdescription?: string | null; uom?: string | null;
  quantity: number; listprice?: number; floorprice?: number;
  discountpercent?: number; unitprice: number; linetotal: number; isbelowfloor: boolean;
}
interface PendingOrder {
  salesorderid: number; sonumber: string; cardcode: string; cardname: string; currency?: string;
  totalnetamount: number; hasbelowfloorline: boolean; creditcheckstatus?: string; submitteddate?: string;
  ponumber?: string;
  /** Salesperson who raised the order; null when the creator was removed. */
  createdbyname?: string | null;
  lines: Line[];
}

/** A line the approver re-priced, with the numbers the warning dialog shows. */
interface PriceChange {
  line: Line;
  from: number;
  to: number;
  floor: number | null;
  belowFloorPct: number;
  discountPct: number;
}

const fmt = (n: number) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n: number) => `${n.toFixed(2)}%`;

function SalesApprovalQueue() {
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [tab, setTab] = useState<'PendingApproval' | 'Failed'>('PendingApproval');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rejectFor, setRejectFor] = useState<PendingOrder | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  // Approver price overrides, keyed by line id. Held as raw strings so the
  // field stays editable mid-typing ("", "1.", "-"); parsed on use.
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [confirmFor, setConfirmFor] = useState<PendingOrder | null>(null);
  // Cancelling a Failed order is Admin-only (the server enforces it too).
  const isAdmin = useSelector((s: any) => s.auth.user?.role) === 'Admin';
  const [cancelFor, setCancelFor] = useState<PendingOrder | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' } | null>(null);

  const notify = useCallback((msg: string, sev: 'success' | 'error' | 'warning' = 'error') => setToast({ msg, sev }), []);
  const isFailedView = tab === 'Failed';

  /** Price currently shown for a line — the approver's edit if valid, else the submitted price. */
  const effectiveUnit = useCallback((l: Line): number => {
    const raw = edits[l.lineid];
    if (raw === undefined) return Number(l.unitprice);
    const n = Number(raw);
    return raw.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : Number(l.unitprice);
  }, [edits]);

  /** Lines whose price the approver actually changed. */
  const changesFor = useCallback((o: PendingOrder): PriceChange[] => (
    o.lines
      .map((l) => {
        const from = Number(l.unitprice);
        const to = effectiveUnit(l);
        const floor = l.floorprice != null ? Number(l.floorprice) : null;
        const list = l.listprice != null ? Number(l.listprice) : null;
        return {
          line: l, from, to, floor,
          belowFloorPct: floor && floor > 0 && to < floor ? ((floor - to) / floor) * 100 : 0,
          discountPct: list && list > 0 && to < list ? ((list - to) / list) * 100 : 0,
        };
      })
      .filter((c) => Math.abs(c.from - c.to) > 0.005)
  ), [effectiveUnit]);

  const newTotalFor = useCallback((o: PendingOrder): number => (
    o.lines.reduce((sum, l) => sum + Number(l.quantity) * effectiveUnit(l), 0)
  ), [effectiveUnit]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res: any = await apiService.getApprovalQueue(companyId, { status: tab });
      setOrders(res.data || []);
      setSelected(new Set());
      setEdits({});
    } catch (e: any) { notify(e.response?.data?.message || 'Failed to load approvals'); }
    finally { setLoading(false); }
  }, [companyId, tab, notify]);

  useEffect(() => { void load(); }, [load]);
  // Group by salesperson for the queue. The server already orders by
  // salesperson then submitted date, so insertion order into the Map is the
  // display order — no re-sorting here, and one group when nobody is named.
  const grouped = useMemo(() => {
    const g = new Map<string, PendingOrder[]>();
    for (const o of orders) {
      const key = o.createdbyname?.trim() || 'Unassigned';
      const list = g.get(key) ?? [];
      list.push(o);
      g.set(key, list);
    }
    return Array.from(g.entries());
  }, [orders]);

  // Bulk approve never carries price edits, so an order being re-priced must be
  // approved individually — otherwise the edits would be silently discarded.
  const eligibleForBulk = (o: PendingOrder) => !o.hasbelowfloorline && changesFor(o).length === 0;
  const toggleSelect = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const approve = async (o: PendingOrder) => {
    const changes = changesFor(o);
    setBusy(true);
    try {
      const res: any = await apiService.approveSalesOrder(
        o.salesorderid,
        changes.map((c) => ({ lineId: c.line.lineid, unitPrice: c.to }))
      );
      if (res.data?.status === 'Failed') notify(`Approved, but SAP post failed: ${res.data?.sapError || ''}`, 'warning');
      else notify(`Approved — SAP ${res.data?.sapDocNum || ''}`, 'success');
      setConfirmFor(null);
      await load();
    } catch (e: any) {
      // 422 carries the server's re-derived rule failures (e.g. the price is
      // further below floor than the approver ceiling allows).
      const errs = e.response?.status === 422 ? e.response.data?.errors : null;
      notify(Array.isArray(errs) && errs.length ? errs.join('; ') : (e.response?.data?.message || 'Approve failed'));
    }
    finally { setBusy(false); }
  };

  /** Approve directly when nothing was re-priced; otherwise show the override warning first. */
  const requestApprove = (o: PendingOrder) => {
    if (changesFor(o).length === 0) { void approve(o); return; }
    setConfirmFor(o);
  };

  const doReject = async () => {
    if (!rejectFor) return;
    setBusy(true);
    try {
      await apiService.rejectSalesOrder(rejectFor.salesorderid, rejectComment.trim());
      notify('Order rejected', 'success');
      setRejectFor(null); setRejectComment('');
      await load();
    } catch (e: any) { notify(e.response?.data?.message || 'Reject failed'); }
    finally { setBusy(false); }
  };

  const retry = async (id: number) => {
    setBusy(true);
    try {
      const res: any = await apiService.retrySalesOrderSap(id);
      if (res.data?.status === 'Approved') notify(`Posted to SAP ${res.data?.sapDocNum || ''}`, 'success');
      else notify(`SAP post failed: ${res.data?.sapError || ''}`, 'warning');
      await load();
    } catch (e: any) { notify(e.response?.data?.message || 'Retry failed'); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!cancelFor) return;
    setBusy(true);
    try {
      await apiService.cancelFailedSalesOrder(cancelFor.salesorderid, cancelReason.trim());
      notify(`${cancelFor.sonumber} cancelled`, 'success');
      setCancelFor(null); setCancelReason('');
      await load();
    } catch (e: any) { notify(e.response?.data?.message || 'Cancel failed'); }
    finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    const ids = [...selected];
    if (ids.length === 0) { notify('Select at least one order'); return; }
    setBusy(true);
    try {
      const res: any = await apiService.bulkApproveSalesOrders(ids);
      const okCount = (res.data || []).filter((r: any) => r.ok).length;
      const failed = (res.data || []).filter((r: any) => !r.ok);
      notify(`${okCount} approved${failed.length ? `, ${failed.length} skipped/failed` : ''}`, failed.length ? 'warning' : 'success');
      await load();
    } catch (e: any) { notify(e.response?.data?.message || 'Bulk approve failed'); }
    finally { setBusy(false); }
  };

  if (!companyId) {
    return <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Select a company (top bar) to review approvals.</Alert></Box>;
  }

  const confirmChanges = confirmFor ? changesFor(confirmFor) : [];
  const confirmHasBelowFloor = confirmChanges.some((c) => c.belowFloorPct > 0);


  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Typography variant="h5" component="h1">Sales Order Approvals</Typography>
        {!isFailedView && (
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => void bulkApprove()} disabled={busy || selected.size === 0}>
            Bulk Approve ({selected.size})
          </Button>
        )}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="PendingApproval" label="Pending" />
        <Tab value="Failed" label="Failed SAP post" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : orders.length === 0 ? (
        <Alert severity="success">{isFailedView ? 'No orders with a failed SAP post.' : 'No orders pending approval.'}</Alert>
      ) : (
        grouped.map(([salesperson, groupOrders]) => (
          <Box key={salesperson} sx={{ mb: 2 }}>
            <Stack
              direction="row" alignItems="baseline" spacing={1}
              sx={{ px: 1, py: 0.75, borderBottom: '2px solid', borderColor: 'divider', mb: 0.5 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{salesperson}</Typography>
              <Typography variant="caption" color="text.secondary">
                {groupOrders.length} order{groupOrders.length === 1 ? '' : 's'} ·{' '}
                {fmt(groupOrders.reduce((sum, g) => sum + Number(g.totalnetamount || 0), 0))}
                {groupOrders[0]?.currency ? ` ${groupOrders[0].currency}` : ''}
              </Typography>
            </Stack>
            {groupOrders.map((o) => (
          <Accordion key={o.salesorderid} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', flexWrap: 'wrap' }}>
                {!isFailedView && (
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    control={<Checkbox
                      checked={selected.has(o.salesorderid)}
                      disabled={!eligibleForBulk(o)}
                      onChange={() => toggleSelect(o.salesorderid)}
                    />}
                    label=""
                    sx={{ mr: 0 }}
                  />
                )}
                <Typography sx={{ fontWeight: 600, minWidth: 130 }}>{o.sonumber}</Typography>
                <Typography sx={{ flex: 1, minWidth: 160 }}>
                  {o.cardname} ({o.cardcode})
                  {o.ponumber && <Typography component="span" variant="body2" color="text.secondary"> · PO {o.ponumber}</Typography>}
                </Typography>
                <Typography sx={{ minWidth: 120, textAlign: 'right' }}>{fmt(o.totalnetamount)} {o.currency || ''}</Typography>
                {o.hasbelowfloorline && <Chip size="small" color="error" label="below floor" />}
                {o.creditcheckstatus === 'Warning' && <Chip size="small" color="warning" label="credit" />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead><TableRow>
                  <TableCell>Item</TableCell><TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell><TableCell>UoM</TableCell>
                  <TableCell align="right">List</TableCell>
                  <TableCell align="right">Floor</TableCell><TableCell align="right">Disc %</TableCell>
                  <TableCell align="right">Net Unit</TableCell><TableCell align="right">Total</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {o.lines.map((l) => {
                    const unit = effectiveUnit(l);
                    const floor = l.floorprice != null ? Number(l.floorprice) : null;
                    const list = l.listprice != null ? Number(l.listprice) : null;
                    const below = floor != null && floor > 0 && unit < floor;
                    const disc = list && list > 0 && unit < list ? ((list - unit) / list) * 100 : 0;
                    const edited = Math.abs(unit - Number(l.unitprice)) > 0.005;
                    return (
                      <TableRow key={l.lineid}>
                        <TableCell>{l.itemcode}{below && <Chip size="small" color="error" label="floor" sx={{ ml: 0.5 }} />}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>{l.itemdescription || '—'}</TableCell>
                        <TableCell align="right">{Number(l.quantity)}</TableCell>
                        <TableCell>{l.uom || '—'}</TableCell>
                        <TableCell align="right">{list != null ? fmt(list) : '—'}</TableCell>
                        <TableCell align="right">{floor != null ? fmt(floor) : '—'}</TableCell>
                        <TableCell align="right">{disc.toFixed(2)}%</TableCell>
                        <TableCell align="right">
                          {isFailedView ? fmt(Number(l.unitprice)) : (
                            <TextField
                              type="number" size="small" variant="standard"
                              value={edits[l.lineid] ?? String(Number(l.unitprice))}
                              onChange={(e) => setEdits((prev) => ({ ...prev, [l.lineid]: e.target.value }))}
                              error={below}
                              inputProps={{ min: 0, step: 0.01, 'aria-label': `Net unit price for ${l.itemcode}`, style: { textAlign: 'right' } }}
                              sx={{ width: 100, ...(edited ? { '& input': { fontWeight: 700 } } : {}) }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{fmt(Number(l.quantity) * unit)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                {!isFailedView && changesFor(o).length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mr: 'auto' }} color="text.secondary">
                      New total: <strong>{fmt(newTotalFor(o))} {o.currency || ''}</strong> (was {fmt(o.totalnetamount)})
                    </Typography>
                    <Button size="small" disabled={busy} onClick={() => setEdits((prev) => {
                      const next = { ...prev };
                      o.lines.forEach((l) => delete next[l.lineid]);
                      return next;
                    })}>Reset prices</Button>
                  </>
                )}
                {isFailedView ? (
                  <>
                    {isAdmin && (
                      <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={() => { setCancelFor(o); setCancelReason(''); }}>Cancel order</Button>
                    )}
                    <Button variant="contained" startIcon={<ReplayIcon />} disabled={busy} onClick={() => void retry(o.salesorderid)}>Retry SAP post</Button>
                  </>
                ) : (
                  <>
                    <Button color="error" startIcon={<CancelIcon />} disabled={busy} onClick={() => { setRejectFor(o); setRejectComment(''); }}>Reject</Button>
                    <Button variant="contained" startIcon={<CheckCircleIcon />} disabled={busy} onClick={() => requestApprove(o)}>Approve</Button>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
            ))}
          </Box>
        ))
      )}

      <ResponsiveDialog open={!!rejectFor} onClose={() => setRejectFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject {rejectFor?.sonumber}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth multiline minRows={3} label="Reason (optional)"
            placeholder="Why is this order being rejected? Leave blank if not needed."
            value={rejectComment} onChange={(e) => setRejectComment(e.target.value)}
            helperText="The reason is included in the email to the salesperson."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectFor(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => void doReject()}>Reject</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!cancelFor} onClose={() => setCancelFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel {cancelFor?.sonumber}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The order leaves the Failed SAP post queue and will not be posted to SAP. It stays on record as Cancelled.
            SAP is checked first; if the order already exists there, it is not cancelled.
          </Alert>
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Reason (optional)"
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            helperText="Saved in the order's approval history."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelFor(null)}>Keep order</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => void doCancel()}>Cancel order</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!confirmFor} onClose={() => setConfirmFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm price override — {confirmFor?.sonumber}</DialogTitle>
        <DialogContent dividers>
          {confirmFor && (() => {
            const changes = changesFor(confirmFor);
            const belowFloor = changes.filter((c) => c.belowFloorPct > 0);
            return (
              <>
                <Alert severity={belowFloor.length ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {belowFloor.length
                    ? `${belowFloor.length} line${belowFloor.length > 1 ? 's are' : ' is'} priced BELOW the floor price. This will be recorded against your name in the approval history.`
                    : 'You are changing the price on this order before it posts to SAP.'}
                </Alert>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead><TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Floor</TableCell>
                      <TableCell align="right">Was</TableCell>
                      <TableCell align="right">New</TableCell>
                      <TableCell align="right">Below floor</TableCell>
                      <TableCell align="right">Discount vs list</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {changes.map((c) => (
                        <TableRow key={c.line.lineid}>
                          <TableCell>{c.line.itemcode}</TableCell>
                          <TableCell align="right">{c.floor != null ? fmt(c.floor) : '—'}</TableCell>
                          <TableCell align="right">{fmt(c.from)}</TableCell>
                          <TableCell align="right"><strong>{fmt(c.to)}</strong></TableCell>
                          <TableCell align="right">
                            {c.belowFloorPct > 0
                              ? <Chip size="small" color="error" label={pct(c.belowFloorPct)} />
                              : '—'}
                          </TableCell>
                          <TableCell align="right">{pct(c.discountPct)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle1">
                    New order total: {fmt(newTotalFor(confirmFor))} {confirmFor.currency || ''}
                    <Typography component="span" variant="body2" color="text.secondary"> (was {fmt(confirmFor.totalnetamount)})</Typography>
                  </Typography>
                </Stack>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFor(null)}>Cancel</Button>
          <Button
            color={confirmHasBelowFloor ? 'error' : 'primary'}
            variant="contained" disabled={busy}
            onClick={() => { if (confirmFor) void approve(confirmFor); }}
          >
            Approve &amp; post to SAP
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default SalesApprovalQueue;
